// MonthlyUsers.js
import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
} from "@mui/material";
import { fetchMonthlyUserAnalytics } from "../../api/user";

const MonthlyUsers = ({ apiUrl = "/api/monthly-users" }) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [data, setData] = useState([]);
  const [onlineUsersCount, setOnlineUsersCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    async function fetchMonthly() {
      setLoading(true);
      try {
        const response = await fetchMonthlyUserAnalytics(`year=${selectedYear}`); // fetchWeeklyUserAnalytics
        const payload = response?.data ?? response;


        // const onlineCount = payload?.onlineUsersCount ?? 0;
        const onlineCount = payload?.pastOnlineUsersCount.reduce((acc, item) => acc + (item.activeUsersCount ?? item.count), 0) ?? 0;
        const slots = payload?.pastOnlineUsersCount ?? [];

        const mapped = slots.map((item) => {
          const [y, m] = (item.date || "").split("-").map(Number);
          const year = Number.isFinite(y) ? y : selectedYear;
          const monthIndex = Number.isFinite(m) ? m - 1 : 0;
          const ts = Date.UTC(year, monthIndex, 1);
          return [ts, Number(item.activeUsersCount ?? item.count ?? 0)];
        });

        setData(mapped);
        setOnlineUsersCount(onlineCount);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("MonthlyUsers fetch error:", err);
        // Optionally display error to user
      } finally {
        setLoading(false);
      }
    }

    fetchMonthly();
    return () => controller.abort();
  }, [selectedYear, apiUrl]);

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleChange = (event) => {
    setSelectedYear(event.target.value);
  };

  return (
    <Box sx={{ p: 3, border: "1px solid #ccc", borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "20px", color: "#000" }}>Monthly Online Users</Typography>

        <FormControl sx={{ mb: 3, minWidth: 120, ms: "auto" }}>
          <InputLabel id="year-select-label">Select Year</InputLabel>
          <Select
            labelId="year-select-label"
            id="year-select"
            value={selectedYear}
            label="Select Year"
            onChange={handleChange}
            sx={{
              height: 40,
              "& .MuiSelect-select": {
                paddingTop: "8px",
                paddingBottom: "8px",
              },
            }}
          >
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
          <CircularProgress size={18} />
          <Typography>Loading monthly users…</Typography>
        </Box>
      )}

      {
        onlineUsersCount
          ? <HighchartsReact
            highcharts={Highcharts}
            options={{
              chart: { backgroundColor: "#fff" },
              title: { text: null },
              subtitle: {
                text: onlineUsersCount,
                align: "left",
                verticalAlign: "top",
                x: 0,
                y: 40,
                style: {
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#000",
                  textTransform: "uppercase",
                },
              },
              credits: { enabled: false },
              xAxis: {
                type: "datetime",
                opposite: true,
                labels: {
                  align: "center",
                  y: -10,
                  formatter() {
                    return Highcharts.dateFormat("%b", this.value);
                  },
                },
                lineWidth: 0,
              },
              yAxis: { title: { text: "" }, labels: { enabled: false }, gridLineWidth: 0 },
              legend: { enabled: false },
              tooltip: { valueSuffix: " Users", xDateFormat: "%B %Y" },
              plotOptions: {
                series: {
                  marker: {
                    radius: 5,
                    fillColor: "#ffffff",
                    lineWidth: 2,
                    lineColor: "#008E40",
                  },
                  lineWidth: 4,
                  shadow: true,
                },
              },
              series: [
                {
                  type: "area",
                  name: "Monthly Online Users",
                  data,
                  color: {
                    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                    stops: [
                      [0, "#008E40"],
                      [1, "#008E40"],
                    ],
                  },
                  fillColor: {
                    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                    stops: [
                      [0, "rgba(0, 142, 64, 0.7)"],
                      [1, "rgba(217, 217, 217, 0)"],
                    ],
                  },
                },
              ],
            }}
          />
          : <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No data available
            </Typography>
          </Box>
      }
    </Box>
  );
};

export default MonthlyUsers;
